/* A customer's letter (see letter.tsx): ruled paper, and each customer's own paper. */

.paper {
  /* Plain ruled paper; each customer's paper below sets these, and may decorate it. */
  --paper: #fffaf3;
  --rule: #f1e2d2;
  --edge: #e3cbb6;
  position: relative;
  padding: 18px 20px;
  border: 1px solid var(--edge);
  border-radius: 6px;
  /* One rule under each line of text, over the paper's colour. */
  background: repeating-linear-gradient(transparent 0 27px, var(--rule) 27px 28px) content-box,
    var(--paper);
  font-size: 14px;
  line-height: 28px;
}

.paper p {
  /* Over the seal or picture, where they meet. */
  position: relative;
  z-index: 1;
  margin: 0;
}

.body p {
  text-indent: 1em;
}

.close {
  text-align: right;
}

/* The customer's face by their name, overhanging its line so the lines stay on the rules. */
.face {
  width: 40px;
  height: 40px;
  margin: -6px 6px -6px 0;
  vertical-align: middle;
}

/* A seal or a picture pressed in a corner of the paper. */
.paper::after {
  position: absolute;
  pointer-events: none;
}

/* The brewery: washi, flecked with fibres, and the brewery's red seal. */
.washi {
  --paper: #fbf5e6;
  --rule: #e9d8bb;
  --edge: #dcc6a2;
  background: repeating-linear-gradient(transparent 0 27px, var(--rule) 27px 28px) content-box,
    radial-gradient(circle at 30% 40%, rgba(150, 110, 60, 0.1) 0 1px, transparent 1.5px) 0 0 /
    23px 19px,
    radial-gradient(circle at 70% 60%, rgba(150, 110, 60, 0.08) 0 1px, transparent 1.5px) 0 0 /
    31px 27px,
    var(--paper);
}

.washi::after {
  content: "酒";
  left: 18px;
  bottom: 16px;
  width: 30px;
  height: 30px;
  border: 2px solid #c8483c;
  border-radius: 4px;
  color: #c8483c;
  font-size: 17px;
  font-weight: 700;
  line-height: 26px;
  text-align: center;
  opacity: 0.8;
  transform: rotate(-8deg);
}

/* The kittens of the north village: pale blue, snowflakes along the top. */
.snowKids {
  --paper: #f5fbff;
  --rule: #d9eaf6;
  --edge: #bcd9ee;
  padding-top: 34px;
  color: #3d5470;
  background: repeating-linear-gradient(transparent 0 27px, var(--rule) 27px 28px) content-box,
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='18' viewBox='0 0 24 18'%3E%3Cg stroke='%238fbde0' stroke-width='1.3' stroke-linecap='round'%3E%3Cpath d='M12 3v12M6.8 6l10.4 6M6.8 12l10.4-6'/%3E%3C/g%3E%3C/svg%3E")
    12px 9px / 24px 18px repeat-x padding-box,
    var(--paper);
}

/* The nursery: a crayon letter, its lines drawn in dashes, a sun in the corner. */
.crayon {
  --paper: #fffbe6;
  --edge: #f4a261;
  border: 2px dashed var(--edge);
  border-radius: 14px;
  background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='28'%3E%3Crect y='26.5' width='6' height='1.5' rx='0.75' fill='%23f3c98b'/%3E%3C/svg%3E")
    0 0 / 10px 28px content-box,
    var(--paper);
}

.crayon::after {
  content: "";
  top: 8px;
  right: 10px;
  width: 38px;
  height: 38px;
  background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 40 40'%3E%3Cg stroke='%23f4a24a' stroke-width='2.5' stroke-linecap='round'%3E%3Ccircle cx='20' cy='20' r='8' fill='%23ffd66b'/%3E%3Cpath d='M20 4v5M20 31v5M4 20h5M31 20h5M8.7 8.7l3.5 3.5M27.8 27.8l3.5 3.5M8.7 31.3l3.5-3.5M27.8 12.2l3.5-3.5'/%3E%3C/g%3E%3C/svg%3E")
    center / contain no-repeat;
}

/* The bean wholesaler: kraft paper and the shop's bean stamp. */
.kraft {
  --paper: #ead5b0;
  --rule: #d4b889;
  --edge: #c7a674;
  color: #45301f;
}

.kraft::after {
  content: "";
  left: 16px;
  bottom: 14px;
  width: 34px;
  height: 26px;
  opacity: 0.85;
  background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 30 22'%3E%3Cpath d='M6 13c-3-6 3-11 8-8 2 1 3 1 5 0 5-3 10 3 6 8-3 5-8 6-11 4-3-2-6 1-8-4z' fill='%23a8743f'/%3E%3Cpath d='M14 6c1 3 1 7 0 11' stroke='%23d9b27c' stroke-width='1.2' fill='none'/%3E%3C/svg%3E")
    center / contain no-repeat;
}

/* The town café: cream paper, a cup set down on it in the corner. */
.cafe {
  --paper: #fffaf0;
  --rule: #efdcc4;
  --edge: #e2c9a8;
}

.cafe::after {
  content: "";
  top: -12px;
  right: -10px;
  width: 78px;
  height: 78px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    transparent 56%,
    rgba(140, 90, 50, 0.2) 59%,
    rgba(140, 90, 50, 0.08) 64%,
    transparent 67%
  );
}

/* The park office: squared paper, a leaf for its letterhead. */
.grid {
  --paper: #fbfffa;
  --rule: #d8ecd3;
  --edge: #bddcb6;
  background: repeating-linear-gradient(transparent 0 27px, var(--rule) 27px 28px) content-box,
    repeating-linear-gradient(90deg, transparent 0 27px, var(--rule) 27px 28px) content-box,
    var(--paper);
}

.grid::after {
  content: "";
  left: 14px;
  bottom: 12px;
  width: 30px;
  height: 30px;
  background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 28 28'%3E%3Cpath d='M5 23C5 12 12 5 23 5c0 11-7 18-18 18z' fill='%239ccf8f'/%3E%3Cpath d='M5 23 17 11' stroke='%236fae62' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E")
    center / contain no-repeat;
}

/* The shopping street: red and white bands for a festive notice. */
.festive {
  --paper: #fffaf7;
  --rule: #f5dad4;
  --edge: #e8b9b0;
  padding-top: 30px;
  padding-bottom: 30px;
  background: repeating-linear-gradient(transparent 0 27px, var(--rule) 27px 28px) content-box,
    repeating-linear-gradient(-45deg, #d9534f 0 7px, #fff 7px 14px) top / 100% 10px no-repeat
    padding-box,
    repeating-linear-gradient(-45deg, #d9534f 0 7px, #fff 7px 14px) bottom / 100% 10px no-repeat
    padding-box,
    var(--paper);
}

/* The snow festival committee: a letterhead, the committee's name on a blue band. */
.letterhead {
  --paper: #fcfdff;
  --rule: #dfe9f4;
  --edge: #bcd2e8;
  padding-top: 46px;
}

.letterhead::before {
  content: attr(data-from);
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 28px;
  border-radius: 5px 5px 0 0;
  background: #6d9fc9;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  line-height: 28px;
  letter-spacing: 0.12em;
  text-align: center;
}

/* The tumulus preservation society: old paper browned at the edges, a clay-red stamp. */
.parchment {
  --paper: #f2e2c2;
  --rule: #dac396;
  --edge: #c7a874;
  color: #45301c;
  background: repeating-linear-gradient(transparent 0 27px, var(--rule) 27px 28px) content-box,
    radial-gradient(ellipse at center, transparent 55%, rgba(120, 80, 30, 0.2) 100%) padding-box,
    var(--paper);
}

.parchment::after {
  content: "保";
  left: 18px;
  bottom: 16px;
  width: 30px;
  height: 30px;
  border: 2px solid #b8643d;
  border-radius: 50%;
  color: #b8643d;
  font-size: 15px;
  font-weight: 700;
  line-height: 26px;
  text-align: center;
  opacity: 0.8;
  transform: rotate(10deg);
}

/* The bakery: cream paper under a band of gingham, and an ear of wheat. */
.bakery {
  --paper: #fffaf0;
  --rule: #f0dfc4;
  --edge: #e2c49a;
  padding-top: 34px;
}

.bakery::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 16px;
  border-radius: 5px 5px 0 0;
  background: repeating-linear-gradient(90deg, rgba(196, 128, 64, 0.45) 0 8px, transparent 8px
    16px),
    repeating-linear-gradient(rgba(196, 128, 64, 0.45) 0 8px, transparent 8px 16px), #fff4e2;
}

.bakery::after {
  content: "";
  top: 26px;
  right: 14px;
  width: 24px;
  height: 36px;
  background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 30'%3E%3Cpath d='M10 29V8' stroke='%23c48a45' stroke-width='1.4' stroke-linecap='round'/%3E%3Cg fill='%23dcaa5e'%3E%3Cellipse cx='10' cy='5' rx='2' ry='3.4'/%3E%3Cellipse cx='7' cy='11' rx='2' ry='3.4' transform='rotate(-35 7 11)'/%3E%3Cellipse cx='13' cy='11' rx='2' ry='3.4' transform='rotate(35 13 11)'/%3E%3Cellipse cx='7' cy='16.5' rx='2' ry='3.4' transform='rotate(-35 7 16.5)'/%3E%3Cellipse cx='13' cy='16.5' rx='2' ry='3.4' transform='rotate(35 13 16.5)'/%3E%3Cellipse cx='7' cy='22' rx='2' ry='3.4' transform='rotate(-35 7 22)'/%3E%3Cellipse cx='13' cy='22' rx='2' ry='3.4' transform='rotate(35 13 22)'/%3E%3C/g%3E%3C/svg%3E")
    center / contain no-repeat;
}

/* The boutique: blush paper under a black band edged in gold, and a gold hanger. */
.boutique {
  --paper: #fffafc;
  --rule: #f3dde7;
  --edge: #e3bfcf;
  padding-top: 30px;
}

.boutique::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 12px;
  border-bottom: 2px solid #d1a74a;
  border-radius: 5px 5px 0 0;
  background: #2f2a33;
}

.boutique::after {
  content: "";
  left: 16px;
  bottom: 14px;
  width: 36px;
  height: 26px;
  background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 36 26'%3E%3Cpath d='M18 10V8a3 3 0 1 0-3-3M18 10 3.5 20.5Q2 22 4.5 22h27q2.5 0 1-1.5z' fill='none' stroke='%23c99a3e' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")
    center / contain no-repeat;
}

/* The near-future club: pale silver paper under a thin blue line, a circuit in the corner. */
.future {
  --paper: #f7f9fc;
  --rule: #dde5f0;
  --edge: #b9c7da;
  padding-top: 30px;
}

.future::before {
  content: "";
  position: absolute;
  top: 10px;
  left: 20px;
  right: 20px;
  height: 3px;
  border-radius: 2px;
  background: linear-gradient(90deg, #3f8fd2, #8fd0f0 60%, transparent);
}

.future::after {
  content: "";
  right: 14px;
  bottom: 12px;
  width: 34px;
  height: 34px;
  opacity: 0.8;
  background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 34 34'%3E%3Cg fill='none' stroke='%237fa6cf' stroke-width='1.6' stroke-linecap='round'%3E%3Cpath d='M4 30h10l6-6V10M20 16h10M14 30V20'/%3E%3C/g%3E%3Cg fill='%233f8fd2'%3E%3Ccircle cx='20' cy='8' r='2.4'/%3E%3Ccircle cx='31' cy='16' r='2.4'/%3E%3Ccircle cx='14' cy='18' r='2.4'/%3E%3C/g%3E%3C/svg%3E")
    center / contain no-repeat;
}

/* The kitchenware shop: mint paper and a rice ball for a stamp. */
.kitchen {
  --paper: #f6fcf6;
  --rule: #d5ebd6;
  --edge: #a9d2ad;
}

.kitchen::after {
  content: "";
  left: 16px;
  bottom: 14px;
  width: 30px;
  height: 28px;
  opacity: 0.9;
  background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 30 28'%3E%3Cpath d='M15 2C10 2 2 17 2 22c0 3 2 4 5 4h16c3 0 5-1 5-4C28 17 20 2 15 2z' fill='%23fff' stroke='%2391c49a' stroke-width='1.5'/%3E%3Crect x='8' y='16' width='14' height='10' rx='1.5' fill='%233c5a46'/%3E%3C/svg%3E")
    center / contain no-repeat;
}

/* The forest archive: an index card, a red line under its head, a pine for its mark. */
.archive {
  --paper: #fffef7;
  --rule: #d6e3ec;
  --edge: #cfc3a6;
  padding-top: 34px;
}

.archive::before {
  content: "";
  position: absolute;
  top: 26px;
  left: 0;
  right: 0;
  border-top: 2px solid rgba(200, 80, 70, 0.55);
}

.archive::after {
  content: "";
  top: 4px;
  right: 14px;
  width: 20px;
  height: 20px;
  background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3E%3Cpath d='M10 1 4 9h3l-4 6h6v4h2v-4h6l-4-6h3z' fill='%236f9a63'/%3E%3C/svg%3E")
    center / contain no-repeat;
}

/* The craft shop: stitched round the edge, and a button sewn on in the corner. */
.stitch {
  --paper: #fffafb;
  --rule: #f3dfe4;
  --edge: #e6bfca;
  outline: 1.5px dashed #d98ea1;
  outline-offset: -7px;
}

.stitch::after {
  content: "";
  right: 14px;
  bottom: 14px;
  width: 28px;
  height: 28px;
  background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 28 28'%3E%3Ccircle cx='14' cy='14' r='12' fill='%23e9a7b6'/%3E%3Ccircle cx='14' cy='14' r='9' fill='none' stroke='%23d0889a' stroke-width='1.2'/%3E%3Cg fill='%23fff'%3E%3Ccircle cx='10.5' cy='10.5' r='1.8'/%3E%3Ccircle cx='17.5' cy='10.5' r='1.8'/%3E%3Ccircle cx='10.5' cy='17.5' r='1.8'/%3E%3Ccircle cx='17.5' cy='17.5' r='1.8'/%3E%3C/g%3E%3Cpath d='M10.5 10.5l7 7M17.5 10.5l-7 7' stroke='%23a0606e' stroke-width='1.2'/%3E%3C/svg%3E")
    center / contain no-repeat;
}
